import { useMutation } from "@apollo/client";
import { Session } from "@app/shared/types";
import { Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { analyticsTrack } from "app/analytics/track";
import {
    GRAPHQL_MUTATION_POST_CANCEL_SESSION_RSVP,
    GRAPHQL_MUTATION_POST_SESSION_RSVP,
    GRAPHQL_QUERY_EVENT_RSVP_USERS,
} from "app/queries";
import { titleFont } from "app/theme";
import { CSDialog } from "components/CSDialog";
import { LoggedInUserProfileContext } from "features/auth/RequireAuthentication";
import calendarIcon from "images/icons/calendar-icon.svg";
import checkIcon from "images/icons/check.svg";
import _ from "lodash";
import { useContext, useState } from "react";
import { UserProfile } from "react-redux-firebase";
import { getEventAnalyticsData } from "./AddToCalendarButton";
import { AddToSingleCalendarButton, CalendarType } from "./AddToSingleCalendarButton";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { routes } from "app/routes";
import { useLocation } from "react-router";

const useStyles = makeStyles((theme) => ({
    root: {
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    icon: {
        marginRight: theme.spacing(2),
    },
    rsvpModal: {
        minHeight: 300,
        textAlign: "center",
    },
    cancelRsvp: {
        background: theme.palette.primary500,
    },
    rsvpButton: {
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
}));

const calendarTypes: CalendarType[] = [
    CalendarType.apple,
    CalendarType.google,
    CalendarType.outlookcom,
    CalendarType.yahoo,
];

export const RsvpButton = (props: {
    event: Session;
    classes?: Partial<ReturnType<typeof useStyles>>;
    rsvpUsers?: UserProfile[];
}) => {
    const { event } = props;
    const classes = useStyles(props);
    const [rvspModalOpen, setRsvpModalOpen] = useState(false);
    const user = useContext(LoggedInUserProfileContext);

    const dispatch = useDispatch();
    const location = useLocation();
    const currentPath = location.pathname;
    const isInOnboardingJourney = currentPath === routes.setupWelcomeSession();

    const [callPostSessionRsvp] = useMutation(GRAPHQL_MUTATION_POST_SESSION_RSVP, {
        refetchQueries: [{ query: GRAPHQL_QUERY_EVENT_RSVP_USERS, variables: { id: event.id } }],
    });

    const postSessionRsvp = () => {
        callPostSessionRsvp({
            variables: {
                sessionId: event.id,
                sendEmail: true,
            },
        });
    };

    const [callPostCancelSessionRsvp] = useMutation(GRAPHQL_MUTATION_POST_CANCEL_SESSION_RSVP, {
        refetchQueries: [{ query: GRAPHQL_QUERY_EVENT_RSVP_USERS, variables: { id: event.id } }],
    });

    const postCancelSessionRsvp = () => {
        callPostCancelSessionRsvp({
            variables: {
                sessionId: event.id,
            },
        });
    };

    const userIsAttending = () => {
        const rsvpForUser = _.find(props.rsvpUsers, {
            id: user.id,
        });

        return rsvpForUser;
    };

    const logEvent = (eventName: string, additionalData: Object = {}) => {
        analyticsTrack(eventName, {
            ...additionalData,
            ...getEventAnalyticsData(event),
        });
    };

    return (
        <div className={classes.root}>
            {userIsAttending() ? (
                <>
                    <Button
                        variant="primary"
                        size="small"
                        className={classes.cancelRsvp}
                        data-testid="rsvpCancelButton"
                        onClick={() => {
                            postCancelSessionRsvp();
                            logEvent("member.community.rsvp.cancel");
                        }}
                    >
                        <img height="25" className={classes.icon} src={checkIcon} alt="" />
                        You plan to attend
                    </Button>
                </>
            ) : (
                <Button
                    variant="primary"
                    size="small"
                    data-testid="rsvpButton"
                    className={classes.rsvpButton}
                    onClick={() => {
                        setRsvpModalOpen(true);
                        postSessionRsvp();
                        logEvent("member.community.rsvp.click");
                    }}
                >
                    <img height="25" className={classes.icon} src={calendarIcon} alt="" />
                    Attend
                </Button>
            )}

            <CSDialog
                onClose={() => {
                    setRsvpModalOpen(false);
                    // if the user is in the setup/onboarding flow, we want to automatically redirect them after they rsvp'd to a welcome session
                    isInOnboardingJourney && dispatch(push(routes.setupShowYouAround()));
                }}
                open={rvspModalOpen}
            >
                <div data-testid="rsvpModal" className={classes.rsvpModal}>
                    <Typography variant="h4">Add this session to your calendar</Typography>
                    {calendarTypes.map((type) => (
                        <div key={type}>
                            <AddToSingleCalendarButton
                                calendarType={type}
                                event={event}
                                onClick={() => {
                                    logEvent("member.community.rsvp.add", {
                                        rsvpType: "calendar",
                                    });
                                    setRsvpModalOpen(false);
                                    // if the user is in the setup/onboarding flow, we want to automatically redirect them after they rsvp'd to a welcome session
                                    isInOnboardingJourney &&
                                        dispatch(push(routes.setupShowYouAround()));
                                }}
                            />
                        </div>
                    ))}
                </div>
            </CSDialog>
        </div>
    );
};
